// SEC-02: every /api/admin/* route sits behind RequireMFA(12h) on the
// backend (cmd/server/routes.go adminBase, ADR-022 amendment 2026-10-23).
// This module is the admin surface's client half: a cheap probe that tells
// AdminMFAGate whether the session's second factor is fresh enough, in the
// hint shape Clerk's useReverification() recovers from.

import {
  ApiError,
  apiRequest,
  isClerkReverificationHint,
  type ClerkReverificationHint,
  type ReverificationConfig,
} from "../../lib/api";

// Mirrors adminMFAFactorMaxAge (12h) in cmd/server/routes.go.
export const ADMIN_REVERIFICATION: ReverificationConfig = {
  afterMinutes: 12 * 60,
  level: "second_factor",
};

// The lightest admin read; any admin route would answer the same gate.
export const ADMIN_MFA_PROBE_PATH = "/api/admin/scheduled-jobs";

type TokenResolver = (options?: {
  skipCache?: boolean;
}) => Promise<string | null>;

export type AdminMFAProbeResult = "ok" | "mfa_required";

/**
 * Probes the admin MFA gate. Returns the Clerk reverification hint (so a
 * useReverification() wrapper opens Clerk's prompt and retries), "ok" when
 * the gate passes, or "mfa_required" when the session has no second factor
 * at all. Any other failure is rethrown. Always fetches a fresh JWT so a
 * retry after reverification carries the updated fva claim.
 */
export async function probeAdminMFA(
  getToken: TokenResolver,
  secondFactorEnrolled: boolean,
): Promise<AdminMFAProbeResult | ClerkReverificationHint> {
  const token = (await getToken({ skipCache: true })) ?? undefined;
  try {
    const res = await apiRequest<unknown>(ADMIN_MFA_PROBE_PATH, {
      allowReverificationOnSecondFactorSetupRequired: secondFactorEnrolled,
      reverification: ADMIN_REVERIFICATION,
      token,
    });
    return isClerkReverificationHint(res) ? res : "ok";
  } catch (err) {
    if (err instanceof ApiError && err.payload?.code === "mfa_required") {
      return "mfa_required";
    }
    throw err;
  }
}

/** True when an MFA_REQUIRED_EVENT came from an admin API call. */
export function isAdminApiPath(path: unknown): boolean {
  return typeof path === "string" && path.startsWith("/api/admin/");
}
